import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { API, useAuth } from '@/App';
import { Users, Fuel, Settings, MapPin, CheckCircle } from 'lucide-react';
import { WhatsAppBookButton } from '@/components/WhatsAppBookButton';

const CarDetailPage = () => {
  const { carId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingData, setBookingData] = useState({
    start_date: '',
    end_date: '',
    with_driver: false
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchCar();
  }, [carId]);

  const fetchCar = async () => {
    try {
      const response = await axios.get(`${API}/cars/${carId}`);
      setCar(response.data);
    } catch (error) {
      toast.error('Failed to load car details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const calculateDays = () => {
    if (!bookingData.start_date || !bookingData.end_date) return 0;
    const start = new Date(bookingData.start_date);
    const end = new Date(bookingData.end_date);
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    return days > 0 ? days : 0;
  };

  const calculateTotal = () => {
    if (!car) return 0;
    const dailyRate = bookingData.with_driver 
      ? (car.with_driver_price || car.price_per_day) 
      : car.price_per_day;
    return calculateDays() * dailyRate;
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Please login to book a car');
      navigate('/login');
      return;
    }

    if (!bookingData.start_date || !bookingData.end_date) {
      toast.error('Please select start and end dates');
      return;
    }

    if (new Date(bookingData.start_date) > new Date(bookingData.end_date)) {
      toast.error('End date must be after start date');
      return;
    }

    setSubmitting(true);

    try {
      // Create booking
      const bookingResponse = await axios.post(`${API}/bookings`, {
        car_id: carId,
        start_date: new Date(bookingData.start_date).toISOString(),
        end_date: new Date(bookingData.end_date).toISOString(),
        with_driver: bookingData.with_driver
      });

      const booking = bookingResponse.data;

      // Create checkout session
      const checkoutResponse = await axios.post(`${API}/payments/checkout`, {
        booking_id: booking.id,
        origin_url: window.location.origin
      });

      // Redirect to Stripe checkout
      window.location.href = checkoutResponse.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
      console.error(error);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p>Loading car details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!car) {
    return (
      <div>
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-xl mb-4">Car not found</p>
            <Link to="/cars">
              <Button>Back to Cars</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Breadcrumb */}
        <div className="mb-8 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-primary">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/cars" className="hover:text-primary">Cars</Link>
          <span className="mx-2">/</span>
          <span>{car.name}</span>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Car Image and Details */}
          <div>
            <div className="rounded-lg overflow-hidden mb-6" data-testid="car-detail-image">
              <img
                src={car.image_url}
                alt={car.name}
                className="w-full h-auto object-cover"
              />
            </div>

            <div className="glass-card p-8 rounded-lg">
              <p className="text-xs uppercase tracking-wider text-accent font-mono mb-2">{car.type}</p>
              <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Cabinet Grotesk' }} data-testid="car-detail-name">
                {car.name}
              </h1>
              
              <div className="flex items-center gap-2 mb-6 text-muted-foreground">
                <MapPin className="w-4 h-4" />
                <span data-testid="car-detail-location">{car.location}</span>
              </div>

              <p className="text-lg mb-8" data-testid="car-detail-description">{car.description}</p>

              <div className="grid grid-cols-3 gap-6 mb-8">
                <div>
                  <Users className="w-6 h-6 text-accent mb-2" />
                  <p className="text-sm text-muted-foreground">Seats</p>
                  <p className="font-semibold" data-testid="car-detail-seats">{car.seats}</p>
                </div>
                <div>
                  <Settings className="w-6 h-6 text-accent mb-2" />
                  <p className="text-sm text-muted-foreground">Transmission</p>
                  <p className="font-semibold" data-testid="car-detail-transmission">{car.transmission}</p>
                </div>
                <div>
                  <Fuel className="w-6 h-6 text-accent mb-2" />
                  <p className="text-sm text-muted-foreground">Fuel Type</p>
                  <p className="font-semibold" data-testid="car-detail-fuel">{car.fuel_type}</p>
                </div>
              </div>

              <div>
                <h3 className="font-bold mb-3">Features</h3>
                <div className="grid grid-cols-2 gap-2">
                  {car.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2" data-testid={`car-feature-${index}`}>
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Booking Form */}
          <div>
            <div className="glass-card p-8 rounded-lg sticky top-24" data-testid="booking-form">
              <div className="mb-6">
                <p className="text-4xl font-bold text-primary" data-testid="car-detail-price">
                  Rs. {car.price_per_day.toLocaleString()}
                  <span className="text-lg font-normal text-muted-foreground"> / day</span>
                </p>
              </div>

              <form onSubmit={handleBooking}>
                {/* Driver Option Selection */}
                <div className="mb-6">
                  <Label className="mb-3 block font-semibold">Choose Rental Option</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setBookingData({ ...bookingData, with_driver: false })}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        !bookingData.with_driver
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid="option-self-drive"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <div className={`w-4 h-4 rounded-full border-2 ${!bookingData.with_driver ? 'border-primary bg-primary' : 'border-border'}`}>
                          {!bookingData.with_driver && <div className="w-full h-full rounded-full bg-primary"></div>}
                        </div>
                        <span className="font-bold text-sm">Self Drive</span>
                      </div>
                      <p className="text-xs text-muted-foreground">Drive it yourself</p>
                      <p className="text-sm font-semibold mt-2 text-primary">Rs. {car.price_per_day.toLocaleString()}/day</p>
                    </button>

                    <button
                      type="button"
                      onClick={() => setBookingData({ ...bookingData, with_driver: true })}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        bookingData.with_driver
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid="option-with-driver"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <div className={`w-4 h-4 rounded-full border-2 ${bookingData.with_driver ? 'border-primary bg-primary' : 'border-border'}`}>
                          {bookingData.with_driver && <div className="w-full h-full rounded-full bg-primary"></div>}
                        </div>
                        <span className="font-bold text-sm">With Driver</span>
                      </div>
                      <p className="text-xs text-muted-foreground">Professional chauffeur</p>
                      <p className="text-sm font-semibold mt-2 text-primary">
                        Rs. {(car.with_driver_price || car.price_per_day).toLocaleString()}/day
                      </p>
                    </button>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <Label htmlFor="start_date">Start Date</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={bookingData.start_date}
                      onChange={(e) => setBookingData({ ...bookingData, start_date: e.target.value })}
                      min={new Date().toISOString().split('T')[0]}
                      required
                      data-testid="booking-start-date"
                    />
                  </div>

                  <div>
                    <Label htmlFor="end_date">End Date</Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={bookingData.end_date}
                      onChange={(e) => setBookingData({ ...bookingData, end_date: e.target.value })}
                      min={bookingData.start_date || new Date().toISOString().split('T')[0]}
                      required
                      data-testid="booking-end-date"
                    />
                  </div>
                </div>

                {calculateDays() > 0 && (
                  <div className="bg-muted p-4 rounded-lg mb-6">
                    <div className="flex justify-between mb-2">
                      <span>Daily Rate:</span>
                      <span data-testid="booking-daily-rate">
                        Rs. {(bookingData.with_driver 
                          ? (car.with_driver_price || car.price_per_day) 
                          : car.price_per_day).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span>Number of Days:</span>
                      <span data-testid="booking-days">{calculateDays()}</span>
                    </div>
                    <div className="flex justify-between mb-2 text-sm text-muted-foreground">
                      <span>Rental Type:</span>
                      <span>{bookingData.with_driver ? 'With Driver' : 'Self Drive'}</span>
                    </div>
                    <div className="flex justify-between text-xl font-bold pt-2 border-t border-border">
                      <span>Total:</span>
                      <span data-testid="booking-total">Rs. {calculateTotal().toLocaleString()}</span>
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full btn-primary text-lg py-6"
                  disabled={submitting}
                  data-testid="booking-submit-btn"
                >
                  {submitting ? 'Processing...' : 'Book Now'}
                </Button>

                {/* Divider */}
                <div className="flex items-center gap-3 my-4">
                  <div className="flex-1 h-px bg-border"></div>
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">or</span>
                  <div className="flex-1 h-px bg-border"></div>
                </div>

                {/* WhatsApp Book Button */}
                <WhatsAppBookButton
                  car={car}
                  startDate={bookingData.start_date}
                  endDate={bookingData.end_date}
                  withDriver={bookingData.with_driver}
                  total={calculateTotal() > 0 ? calculateTotal() : null}
                />

                <p className="text-xs text-center text-muted-foreground mt-2">
                  Quick booking via WhatsApp — instant response
                </p>

                {!user && (
                  <p className="text-sm text-center mt-4 text-muted-foreground">
                    Please <Link to="/login" className="text-primary underline">login</Link> to book this car
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CarDetailPage;
